/**
 * PATH: packages/email-hub/src/feature/components/workbook/composeWorkbookExport.ts
 * FILE_ID: EMAILHUB-WORKBOOK-EXPORT-001
 * PURPOSE: Pure I→O helpers for the composer spreadsheet builder — cell fill
 *          palette, used-bounds trimming, .xlsx export (with fills +
 *          formulas via xlsx-js-style) and email-safe HTML table rendering.
 * WHY: FEAT-2026-07-03-COMPOSE-SPREADSHEET — the operator asked to scaffold
 *      Excel-like tables (with colour) in the composer and attach them.
 *      The grid/engine is the SHARED @quotech/quote-builder/grid stack (same
 *      as the kanban Workbook); this module only owns what the composer adds
 *      on top: colour styling and the two output shapes (file + body HTML).
 * FLOW:
 *   A1MultiSheetWorkbook + fills ─▶ workbookToXlsxBlob ─▶ File (.xlsx)
 *   sheet + computed + fills     ─▶ sheetToHtmlTable   ─▶ inline-styled <table>
 * DEPENDENCIES:
 *   - xlsx-js-style: SheetJS fork that WRITES cell styles (fills) — the CE
 *     xlsx build silently drops them.
 *   - @quotech/quote-builder/grid: A1 helpers + compute engine types.
 * RELATED:
 *   - ComposeWorkbookModal.tsx: the interactive builder that calls these.
 */
import XLSX from 'xlsx-js-style'
import {
  computeA1Workbook,
  indexToCol,
  type A1MultiSheetWorkbook,
  type A1WorkbookSheet,
} from '@quotech/quote-builder/grid'

/** Fill palette — token → swatch. Tokens (not hexes) are stored per cell so
 *  the grid class map, the xlsx fill and the HTML style always agree. */
export const WORKBOOK_FILLS: Record<string, { label: string; hex: string; text: string }> = {
  yellow: { label: 'Yellow', hex: '#FDE68A', text: '#713F12' },
  green: { label: 'Green', hex: '#BBF7D0', text: '#14532D' },
  blue: { label: 'Blue', hex: '#BFDBFE', text: '#1E3A8A' },
  red: { label: 'Red', hex: '#FECACA', text: '#7F1D1D' },
  orange: { label: 'Orange', hex: '#FED7AA', text: '#7C2D12' },
  purple: { label: 'Purple', hex: '#E9D5FF', text: '#581C87' },
  gray: { label: 'Gray', hex: '#E5E7EB', text: '#111827' },
}

/** sheetId → (A1 ref → fill token). Kept OUTSIDE the shared workbook blob so
 *  the persisted kanban shape is untouched — fills are composer-local. */
export type WorkbookFillMap = Record<string, Record<string, string>>

/** The rectangle of cells that actually carry content (or a fill). */
export function usedBounds(
  sheet: A1WorkbookSheet,
  fills: Record<string, string> | undefined,
): { rows: number; cols: number } {
  let rows = 0
  let cols = 0
  const scan = (ref: string) => {
    const m = ref.match(/^([A-Z]+)(\d+)$/)
    if (!m) return
    const c = m[1].split('').reduce((acc, ch) => acc * 26 + (ch.charCodeAt(0) - 64), 0)
    const r = Number(m[2])
    if (r > rows) rows = r
    if (c > cols) cols = c
  }
  for (const ref of Object.keys(sheet.cells)) {
    if ((sheet.cells[ref] ?? '').trim() !== '') scan(ref)
  }
  for (const ref of Object.keys(fills ?? {})) scan(ref)
  return { rows: Math.min(rows, sheet.rows), cols: Math.min(cols, sheet.cols) }
}

/** Windows-safe attachment filename from a free-text base. */
export function workbookFilename(base: string): string {
  const clean = base
    .trim()
    .replace(/[\\/:*?"<>|]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 60)
  return `${clean || 'Spreadsheet'}.xlsx`
}

function cellStyle(token: string | undefined) {
  const fill = token ? WORKBOOK_FILLS[token] : undefined
  if (!fill) return undefined
  return {
    fill: { patternType: 'solid', fgColor: { rgb: fill.hex.slice(1).toUpperCase() } },
    font: { color: { rgb: fill.text.slice(1).toUpperCase() } },
  }
}

/**
 * Workbook → .xlsx File. Formulas survive as REAL Excel formulas (`=SUM…`
 * → `f`), plain numbers as numbers, everything else as text; palette fills
 * become solid-fill cell styles. Pure aside from Blob/File construction.
 */
export function workbookToXlsxFile(
  wb: A1MultiSheetWorkbook,
  fills: WorkbookFillMap,
  filenameBase: string,
): File {
  const book = XLSX.utils.book_new()
  const usedNames = new Set<string>()
  for (const sheet of wb.sheets) {
    const computed = computeA1Workbook(sheet.cells)
    const sheetFills = fills[sheet.id] ?? {}
    const { rows, cols } = usedBounds(sheet, sheetFills)
    const ws: Record<string, unknown> & { '!ref'?: string } = {}
    for (let r = 1; r <= Math.max(rows, 1); r++) {
      for (let c = 0; c < Math.max(cols, 1); c++) {
        const ref = `${indexToCol(c)}${r}`
        const raw = (sheet.cells[ref] ?? '').trim()
        const style = cellStyle(sheetFills[ref])
        if (raw === '' && !style) continue
        const cell: Record<string, unknown> = {}
        if (raw.startsWith('=')) {
          cell.f = raw.slice(1)
          const v = computed.values[ref]
          const n = Number(v)
          if (v !== undefined && v !== '' && Number.isFinite(n)) { cell.v = n; cell.t = 'n' }
          else { cell.v = v ?? ''; cell.t = 's' }
        } else {
          const n = Number(raw)
          if (raw !== '' && Number.isFinite(n)) { cell.v = n; cell.t = 'n' }
          else { cell.v = raw; cell.t = 's' }
        }
        if (style) cell.s = style
        ws[ref] = cell
      }
    }
    ws['!ref'] = `A1:${indexToCol(Math.max(cols, 1) - 1)}${Math.max(rows, 1)}`
    // Unique, Excel-legal sheet name (31 chars, no []:*?/\).
    let name = sheet.name.replace(/[[\]:*?/\\]+/g, ' ').trim().slice(0, 28) || 'Sheet'
    let suffix = 1
    while (usedNames.has(name)) name = `${name.slice(0, 25)}~${++suffix}`
    usedNames.add(name)
    XLSX.utils.book_append_sheet(book, ws, name)
  }
  const out = XLSX.write(book, { type: 'array', bookType: 'xlsx' }) as ArrayBuffer
  return new File([out], workbookFilename(filenameBase), {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/**
 * Active sheet → email-safe HTML table (inline styles only — email clients
 * strip <style> blocks). Shows COMPUTED values (recipients get results, not
 * formulas) with the palette fills. Empty trailing rows/cols are trimmed.
 * Returns '' when the sheet has no content to insert.
 */
export function sheetToHtmlTable(
  sheet: A1WorkbookSheet,
  fills: Record<string, string> | undefined,
): string {
  const computed = computeA1Workbook(sheet.cells)
  const { rows, cols } = usedBounds(sheet, fills)
  if (rows === 0 || cols === 0) return ''
  const border = 'border:1px solid #d1d5db;padding:4px 8px;font-size:13px;'
  const trs: string[] = []
  for (let r = 1; r <= rows; r++) {
    const tds: string[] = []
    for (let c = 0; c < cols; c++) {
      const ref = `${indexToCol(c)}${r}`
      const raw = (sheet.cells[ref] ?? '').trim()
      const display = raw.startsWith('=') ? (computed.values[ref] ?? '') : raw
      const fill = fills?.[ref] ? WORKBOOK_FILLS[fills[ref]] : undefined
      const fillCss = fill ? `background-color:${fill.hex};color:${fill.text};` : ''
      tds.push(`<td style="${border}${fillCss}">${escapeHtml(String(display))}</td>`)
    }
    trs.push(`<tr>${tds.join('')}</tr>`)
  }
  return (
    '<table style="border-collapse:collapse;margin:8px 0;" data-compose-workbook="1">' +
    `<tbody>${trs.join('')}</tbody></table><p></p>`
  )
}
